import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { Session } from '@supabase/supabase-js'
import { supabase, Profile } from '../lib/supabase'
import { logLogin, logLogout } from '../lib/activityLogger'
import { UserRole } from '../types'
import { hasRole } from '../lib/roleUtils'

type SignUpResult = { error: string | null; needsConfirmation: boolean }

type AuthState = {
  session: Session | null
  profile: Profile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signUp: (email: string, password: string, fullName: string, role: Profile['role'] | Profile['role'][]) => Promise<SignUpResult>
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  addRole: (role: UserRole) => Promise<{ error: string | null }>
  removeRole: (role: UserRole) => Promise<{ error: string | null }>
}

const AuthContext = createContext<AuthState>({
  session: null,
  profile: null,
  loading: true,
  signIn: async () => ({ error: 'not implemented' }),
  signUp: async () => ({ error: 'not implemented', needsConfirmation: false }),
  updateProfile: async () => ({ error: 'not implemented' }),
  signOut: async () => {},
  addRole: async () => ({ error: 'not implemented' }),
  removeRole: async () => ({ error: 'not implemented' }),
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadProfile = async (userId: string, userEmail?: string) => {
      try {
        const { data: p, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle()

        if (error) {
          console.error('Error loading profile:', error)
          setProfile(null)
        } else if (!p) {
          // Profile doesn't exist - show error but don't auto-create
          console.error('Profile not found for user:', userId)
          setProfile(null)
        } else {
          // Load roles from user_roles table
          const { data: userRoles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId)

          const roles = userRoles?.map(r => r.role) || []

          // Add email from session if not in profile
          const profileWithEmail = p ? {
            ...p,
            email: p.email || userEmail,
            roles: roles.length > 0 ? roles : undefined // Only set roles if they exist
          } : null
          
          // Check if this user is a business team member
          const { data: businessUserData } = await supabase
            .from('business_users')
            .select('*')
            .eq('user_id', userId)
            .eq('is_active', true)
            .maybeSingle()
          
          if (businessUserData && profileWithEmail) {
            // User is a team member - load the business owner's profile instead
            console.log('User is a team member of business:', businessUserData.business_id)
            const { data: businessProfile, error: businessError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', businessUserData.business_id)
              .maybeSingle()
            
            if (businessProfile && !businessError) {
              // Use business profile but keep the team member's email for display
              setProfile({
                ...businessProfile,
                email: profileWithEmail.email, // Team member's email
                full_name: profileWithEmail.full_name, // Team member's name
                team_role: businessUserData.role, // Add team role info
                team_member_id: userId // Track that this is a team member
              } as Profile)
              return
            }
          }
          
          // Fallback: if tower and is_online is false, set it to true
          if (profileWithEmail && profileWithEmail.role === 'tower' && !profileWithEmail.is_online) {
            console.log('Tower is_online is false during profile load, setting to true');
            await supabase.from('profiles').update({ is_online: true }).eq('id', userId);
            profileWithEmail.is_online = true;
          }
          
          setProfile(profileWithEmail as Profile | null)
        }
      } catch (error) {
        console.error('Error in loadProfile:', error)
        setProfile(null)
      } finally {
        setLoading(false)
      }
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      if (data.session) {
        loadProfile(data.session.user.id, data.session.user.email)
      } else {
        setLoading(false)
      }
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess)
      if (sess) {
        loadProfile(sess.user.id, sess.user.email)
      } else {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => sub.subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    console.log('=== SIGN IN (context/auth.tsx) ===');
    console.log('Email:', email);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    console.log('Auth result:', { error: error?.message, user: data.user?.id });

    if (error) {
      // Convert technical auth errors to user-friendly messages
      if (error.message.includes('Invalid login credentials')) {
        return { error: 'Invalid email or password. Please try again.' }
      }
      if (error.message.includes('Email not confirmed')) {
        return { error: 'Please check your email and confirm your account before signing in.' }
      }
      return { error: error.message }
    }

    if (!data.user) {
      return { error: 'Unable to sign in. Please try again.' }
    }

    console.log('Setting is_online to true for user:', data.user.id);
    try {
      const { error: updateError } = await supabase.from('profiles').update({ is_online: true }).eq('id', data.user.id);
      console.log('is_online update result:', updateError);
      if (updateError) {
        console.error('Failed to set is_online:', updateError);
      }
      // Note: Profile will be refreshed automatically by the auth state change listener
    } catch (e) {
      console.error('Exception setting is_online:', e);
    }
    // Log login activity
    logLogin()

    return { error: null }
  }

  const signUp = async (email: string, password: string, fullName: string, role: Profile['role'] | Profile['role'][]): Promise<SignUpResult> => {
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) {
      // Convert technical auth errors to user-friendly messages
      if (error.message.includes('User already registered')) {
        return { error: 'This email is already registered. Please sign in instead.', needsConfirmation: false }
      }
      if (error.message.includes('Password should be')) {
        return { error: 'Password must be at least 6 characters.', needsConfirmation: false }
      }
      if (error.message.includes('Invalid email')) {
        return { error: 'Please enter a valid email address.', needsConfirmation: false }
      }
      return { error: error.message, needsConfirmation: false }
    }
    if (!data.user) return { error: 'Sign-up failed. Please try again.', needsConfirmation: false }

    // Handle single role or array of roles
    const roles = Array.isArray(role) ? role : [role]
    const primaryRole = roles[0]

    console.log('=== SIGN UP (old context) ===');
    console.log('Roles selected:', roles);
    console.log('Primary role:', primaryRole);
    console.log('Primary role type:', typeof primaryRole);

    // Build profile data - minimal required fields first
    const profileData: any = {
      id: data.user.id,
      role: primaryRole, // Use primary role for backward compatibility
      full_name: fullName,
      email: email, // Save email to profiles table
    }

    console.log('Profile data to insert:', profileData);

    // Business roles need verification and onboarding
    if (primaryRole !== 'customer') {
      profileData.verification_status = 'pending'
      profileData.is_online = false // Disabled until verified
      profileData.onboarding_completed = false // Will be set to true after onboarding
      profileData.registration_submitted_at = new Date().toISOString()
      // Initialize onboarding status
      profileData.verification_notes = JSON.stringify({
        onboarding_status: 'not_started',
        started_at: new Date().toISOString()
      })
    }

    const { data: insertedProfile, error: profileError } = await supabase
      .from('profiles')
      .insert(profileData)
      .select()
      .single()

    if (profileError) {
      console.error('Profile insert error:', profileError);
      // Convert technical database errors to user-friendly messages
      if (profileError.message.includes('violates check constraint')) {
        return { error: 'Invalid role selection. Please select a valid role.', needsConfirmation: false }
      }
      if (profileError.message.includes('duplicate key')) {
        return { error: 'This email is already registered. Please sign in instead.', needsConfirmation: false }
      }
      if (profileError.message.includes('null value')) {
        return { error: 'Please fill in all required fields.', needsConfirmation: false }
      }
      return { error: 'Unable to create account. Please try again later.', needsConfirmation: false }
    }

    // Insert roles into user_roles table for multi-role support
    if (roles.length > 1) {
      const { error: rolesError } = await supabase.from('user_roles').insert(
        roles.map(r => ({ user_id: data.user.id, role: r }))
      );
      if (rolesError) {
        console.error('Failed to insert user roles:', rolesError);
        // Don't fail sign-up, just log the error
      }
    }

    if (data.session && insertedProfile) {
      setProfile(insertedProfile as Profile)
      setLoading(false)
    }

    const needsConfirmation = !data.session

    // If business role, they need to complete onboarding
    if (primaryRole !== 'customer' && !needsConfirmation) {
      // The App.tsx will redirect to /onboarding automatically
    }

    return { error: null, needsConfirmation }
  }

  const signOut = async () => {
    console.log('=== SIGN OUT (context/auth.tsx) ===');
    if (profile) {
      // If this is a team member, update their own profile
      const userIdToUpdate = profile.team_member_id || profile.id
      console.log('Setting is_online to false for user:', userIdToUpdate);
      await supabase.from('profiles').update({ is_online: false }).eq('id', userIdToUpdate);
    }
    // Log logout activity
    logLogout()
    await supabase.auth.signOut()
    setProfile(null)
    setSession(null)
  }

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!profile) return { error: 'Not signed in' }
    const { error } = await supabase.from('profiles').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', profile.id)
    if (error) return { error: error.message }
    setProfile({ ...profile, ...updates })
    return { error: null }
  }

  const addRole = async (role: UserRole): Promise<{ error: string | null }> => {
    if (!session) return { error: 'Not authenticated' };
    if (!profile) return { error: 'Profile not loaded' };

    // Check if user already has this role
    if (hasRole(profile, role)) return { error: 'You already have this role' };

    // Add role to user_roles table
    const { error } = await supabase.from('user_roles').insert({
      user_id: session.user.id,
      role: role
    });

    if (error) {
      console.error('Error adding role:', error);
      // Return user-friendly error message
      if (error.message.includes('row-level security')) {
        return { error: 'Unable to add role. Please contact support.' };
      }
      return { error: 'Failed to add role. Please try again.' };
    }

    return { error: null };
  };

  const removeRole = async (role: UserRole): Promise<{ error: string | null }> => {
    if (!session) return { error: 'Not authenticated' };
    if (!profile) return { error: 'Profile not loaded' };

    // Check if user has this role
    if (!hasRole(profile, role)) return { error: 'You do not have this role' };

    // Cannot remove the last role
    const userRoles = profile.roles || [profile.role];
    if (userRoles.length <= 1) {
      return { error: 'You must have at least one role' };
    }

    // Remove role from user_roles table
    const { error } = await supabase.from('user_roles')
      .delete()
      .eq('user_id', session.user.id)
      .eq('role', role);

    if (error) {
      console.error('Error removing role:', error);
      if (error.message.includes('row-level security')) {
        return { error: 'Unable to remove role. Please contact support.' };
      }
      return { error: 'Failed to remove role. Please try again.' };
    }

    return { error: null };
  };

  return <AuthContext.Provider value={{ session, profile, loading, signIn, signUp, updateProfile, signOut, addRole, removeRole }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}
